import {
  REPORT_TEMPLATE_VERSION,
  reportBrandingSnapshotSchema,
  reportSnapshotSchema,
  safeReportFilename,
  type ReportSnapshot,
} from "@base-hormonal/contracts";
import type { Database } from "@base-hormonal/database";
import type { StorageService } from "../../../shared/api/storage";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import type { Branding, ComposerData } from "../model/reports";

export type ReportExport = Database["public"]["Tables"]["report_exports"]["Row"];
export type BrandingRow = Database["public"]["Tables"]["report_branding"]["Row"];
type Profile = Database["public"]["Tables"]["alunos"]["Row"];
type Assessment = Database["public"]["Tables"]["avaliacoes_corporais"]["Row"] & {
  avaliacao_fotos: Database["public"]["Tables"]["avaliacao_fotos"]["Row"][];
};
export interface ReportSource {
  profile: Profile;
  data: ComposerData & { assessments: Assessment[] };
}
const raise = (error: unknown) => {
  const candidate =
    typeof error === "object" && error !== null ? (error as { code?: string }) : null;
  const safe = fromSupabaseError(candidate);
  if (safe) throw safe;
};

export function createReportsApi(client: TypedSupabaseClient, storage: StorageService) {
  return {
    async branding(userId: string): Promise<BrandingRow | null> {
      const { data, error } = await client
        .from("report_branding")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      raise(error);
      return data;
    },
    async saveBranding(userId: string, branding: Branding): Promise<BrandingRow> {
      const payload = { ...branding, user_id: userId };
      const { data, error } = await client
        .from("report_branding")
        .upsert(payload, { onConflict: "user_id" })
        .select("*")
        .single();
      raise(error);
      if (!data) throw new PublicAppError("empty_response", "A identidade não foi devolvida.");
      return data;
    },
    async saveBrandingLogo(
      userId: string,
      branding: Branding,
      file: File,
      maxBytes?: number | null,
    ): Promise<BrandingRow> {
      return storage.uploadThenCommit(
        {
          bucket: "report-branding",
          segments: [userId],
          file,
          ...(maxBytes === undefined ? {} : { maxBytes }),
        },
        "report_branding",
        async (uploaded) => {
          const payload = { ...branding, user_id: userId, logo_path: uploaded.path };
          const { data, error } = await client
            .from("report_branding")
            .upsert(payload, { onConflict: "user_id" })
            .select("*")
            .single();
          raise(error);
          if (!data) throw new PublicAppError("empty_response", "A identidade não foi devolvida.");
          return data;
        },
      );
    },
    async source(profileId: string): Promise<ReportSource> {
      const [profile, assessments, photos, exams, protocols, nutritionPlans, trainings] =
        await Promise.all([
          client.from("alunos").select("*").eq("id", profileId).single(),
          client
            .from("avaliacoes_corporais")
            .select("*")
            .eq("aluno_id", profileId)
            .order("data", { ascending: false }),
          client.from("avaliacao_fotos").select("*").eq("aluno_id", profileId),
          client
            .from("exames")
            .select("*")
            .eq("aluno_id", profileId)
            .order("created_at", { ascending: false }),
          client
            .from("protocolos")
            .select("*,protocolo_itens!protocolo_itens_protocolo_id_fkey(*)")
            .eq("aluno_id", profileId)
            .order("created_at", { ascending: false }),
          client
            .from("planos_alimentares")
            .select(
              "*,plano_refeicoes!plano_refeicoes_plano_id_aluno_id_fkey(*,plano_refeicao_itens!plano_refeicao_itens_refeicao_id_aluno_id_fkey(*,plano_item_substituicoes!plano_item_substituicoes_item_id_aluno_id_fkey(*)))",
            )
            .eq("aluno_id", profileId)
            .order("created_at", { ascending: false }),
          client
            .from("treinos")
            .select(
              "*,treino_fases!treino_fases_treino_id_aluno_id_fkey(*,treino_dias!treino_dias_fase_id_aluno_id_fkey(*,treino_exercicios!treino_exercicios_dia_id_aluno_id_fkey(*)))",
            )
            .eq("aluno_id", profileId)
            .order("created_at", { ascending: false }),
        ]);
      for (const result of [
        profile,
        assessments,
        photos,
        exams,
        protocols,
        nutritionPlans,
        trainings,
      ])
        raise(result.error);
      if (!profile.data) throw new PublicAppError("not_found", "Perfil não encontrado.");
      const assessmentRows = (assessments.data ?? []).map((row) => ({
        ...row,
        avaliacao_fotos: (photos.data ?? []).filter((photo) => photo.avaliacao_id === row.id),
      }));
      return {
        profile: profile.data,
        data: {
          assessments: assessmentRows as Assessment[],
          exams: exams.data ?? [],
          protocols: protocols.data ?? [],
          nutritionPlans: nutritionPlans.data ?? [],
          trainings: trainings.data ?? [],
        },
      };
    },
    async photoUrls(paths: readonly string[], signal?: AbortSignal) {
      return storage.signedUrls("body-progress", paths, 300, signal);
    },
    async logoUrl(path: string, signal?: AbortSignal) {
      return storage.signedUrl("report-branding", path, 300, signal);
    },
    async logoDataUrl(userId: string, path: string): Promise<string | null> {
      if (!storage.isOwnPath("report-branding", path, userId)) return null;
      const { data, error } = await client.storage.from("report-branding").download(path);
      if (error !== null || data === null) return null;
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(data);
      });
    },
    async list(profileId: string): Promise<ReportExport[]> {
      const { data, error } = await client
        .from("report_exports")
        .select("*")
        .eq("aluno_id", profileId)
        .order("created_at", { ascending: false });
      raise(error);
      return data ?? [];
    },
    async emit(
      userId: string,
      profileId: string,
      title: string,
      snapshot: ReportSnapshot,
      branding: Branding,
      pdf: Blob,
    ): Promise<ReportExport> {
      const stableSnapshot = reportSnapshotSchema.parse(structuredClone(snapshot));
      const stableBranding = reportBrandingSnapshotSchema.parse(structuredClone(branding));
      const file = new File([pdf], safeReportFilename(title), { type: "application/pdf" });
      return storage.uploadThenCommit(
        { bucket: "report-exports", segments: [userId, profileId], file },
        "report_export",
        async (uploaded) => {
          // A v13 exige `storage_path = user/perfil/<id>.pdf`: o id da linha é o
          // mesmo UUID sorteado para o nome do arquivo.
          const id =
            uploaded.path
              .split("/")
              .at(-1)
              ?.replace(/\.pdf$/, "") ?? "";
          const { data, error } = await client
            .from("report_exports")
            .insert({
              id,
              user_id: userId,
              aluno_id: profileId,
              report_type: "perfil_completo",
              title: title.trim().slice(0, 180) || "Relatório",
              selected_sections: stableSnapshot.selected_sections,
              content_snapshot: stableSnapshot,
              branding_snapshot: stableBranding,
              template_version: REPORT_TEMPLATE_VERSION,
              storage_path: uploaded.path,
              file_size_bytes: uploaded.sizeBytes,
              status: "completed",
              period_start: stableSnapshot.period.start,
              period_end: stableSnapshot.period.end,
            })
            .select("*")
            .single();
          raise(error);
          if (!data) throw new PublicAppError("empty_response", "A emissão não foi registrada.");
          return data;
        },
      );
    },
    async download(report: ReportExport): Promise<Blob> {
      if (
        !storage.isOwnPath("report-exports", report.storage_path, report.user_id, report.aluno_id)
      )
        throw new PublicAppError("invalid_path", "Arquivo de relatório inválido.");
      const { data, error } = await client.storage
        .from("report-exports")
        .download(report.storage_path);
      raise(error);
      if (!data) throw new PublicAppError("empty_response", "Arquivo não encontrado.");
      return data;
    },
  };
}
export type ReportsApi = ReturnType<typeof createReportsApi>;
